import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BASE_URL } from '../config/api'; // Assuming BASE_URL is correctly defined

const EditInventorySetup = () => {
    const { id } = useParams(); // Get the inventory setup ID from the URL
    const navigate = useNavigate();

    // State for holding the inventory setup data
    const [inventorySetup, setInventorySetup] = useState({
        itemStockUnit: '',
        warehouseOption: '',
        documentClassification: false,
        barcodeScanLookup: false,
        barcodeScanBasedOn: '',
        minimumStockLevels: false,
        stockValuationMethod: '',
        considerSalesReturnInStockValuation: false,
        itemCodingConfiguration: '',
        duplicateItemBasedOn: '',
        itemSelection: '',
        separatePrintDescription: false,
    });

    // Fetch inventory setup details on component mount
    useEffect(() => {
        fetch(`${BASE_URL}inventorysetup/${id}`)
            .then(response => response.json())
            .then(data => {
                if (data.status === 'success') {
                    setInventorySetup(data.data);
                }
            })
            .catch((error) => console.error('Error fetching inventory setup details:', error));
    }, [id]);

    // Handle form submission
    const handleSubmit = (event) => {
        event.preventDefault();

        fetch(`${BASE_URL}inventorysetup/${id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(inventorySetup),
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            })
            .then((data) => {
                if (data.status === 'success') {
                    alert('Inventory setup updated successfully!');
                    navigate('/EditInventorySetup/1'); // Assuming the route to list is `/InventorySetupList`
                } else {
                    alert('Error updating inventory setup. Please try again.');
                }
            })
            .catch((error) => {
                console.error('Error updating inventory setup:', error);
                alert('Error updating inventory setup. Please check the console for more details.');
            });
    };

    // Handle input change for all fields
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            setInventorySetup({ ...inventorySetup, [name]: checked });
        } else {
            setInventorySetup({ ...inventorySetup, [name]: value });
        }
    };

    return (
        <div id="main">
            <header className="mb-3">
                <a href="#" className="burger-btn d-block d-xl-none">
                    <i className="bi bi-justify fs-3"></i>
                </a>
            </header>

            <div className="page-heading">
                <div className="page-title">
                    <div className="row">
                        <div className="col-12 col-md-6 order-md-1 order-last">
                            <h3>Edit Inventory Setup</h3>
                        </div>
                        <div className="col-12 col-md-6 order-md-2 order-first">
                            <nav aria-label="breadcrumb" className="breadcrumb-header float-start float-lg-end">
                                <ol className="breadcrumb"></ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>

            {/* Edit Inventory Setup Form */}
            <section className="section">
                <div className="row">
                    <div className="col-12 col-md-12">
                        <div className="card">
                            <div className="card-header"></div>
                            <div className="card-body">
                                <form onSubmit={handleSubmit}>
                                    {/* Item Stock Unit Dropdown */}
                                    <div className="mb-3">
                                        <label htmlFor="itemStockUnit" className="form-label">Item Stock Unit</label>
                                        <select
                                            id="itemStockUnit"
                                            name="itemStockUnit"
                                            className="form-control"
                                            value={inventorySetup.itemStockUnit}
                                            onChange={handleChange}
                                        >
                                            <option value="StockUnit">Stock Unit</option>
                                            <option value="Stock_PackUnit">Stock Pack Unit</option>
                                            <option value="Stock_Pack_GPackUnit">Stock Pack GPack Unit</option>
                                        </select>
                                    </div>

                                    {/* Warehouse Option */}
                                    <div className="mb-3">
                                        <label htmlFor="warehouseOption" className="form-label">Warehouse Option</label>
                                        <select
                                            id="warehouseOption"
                                            name="warehouseOption"
                                            className="form-control"
                                            value={inventorySetup.warehouseOption}
                                            onChange={handleChange}
                                        >
                                            <option value="DocumentLevel">Document Level</option>
                                            <option value="WarehouseLevel">Warehouse Level</option>
                                        </select>
                                    </div>

                                    {/* Document Classification */}
                                    <div className="mb-3">
                                        <label htmlFor="documentClassification" className="form-label">Document Classification</label>
                                        <input
                                            type="checkbox"
                                            id="documentClassification"
                                            name="documentClassification"
                                            className="form-check-input"
                                            checked={inventorySetup.documentClassification}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    {/* Barcode Scan Lookup */}
                                    <div className="mb-3">
                                        <label htmlFor="barcodeScanLookup" className="form-label">Barcode Scan Lookup</label>
                                        <input
                                            type="checkbox"
                                            id="barcodeScanLookup"
                                            name="barcodeScanLookup"
                                            className="form-check-input"
                                            checked={inventorySetup.barcodeScanLookup}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    {/* Barcode Scan Based On */}
                                    <div className="mb-3">
                                        <label htmlFor="barcodeScanBasedOn" className="form-label">Barcode Scan Based On</label>
                                        <select
                                            id="barcodeScanBasedOn"
                                            name="barcodeScanBasedOn"
                                            className="form-control"
                                            value={inventorySetup.barcodeScanBasedOn}
                                            onChange={handleChange}
                                        >
                                            <option value="ItemCode">Item Code</option>
                                            <option value="Barcode">Barcode</option>
                                        </select>
                                    </div>

                                    {/* Minimum Stock Levels */}
                                    <div className="mb-3">
                                        <label htmlFor="minimumStockLevels" className="form-label">Minimum Stock Levels</label>
                                        <input
                                            type="checkbox"
                                            id="minimumStockLevels"
                                            name="minimumStockLevels"
                                            className="form-check-input"
                                            checked={inventorySetup.minimumStockLevels}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    {/* Stock Valuation Method */}
                                    <div className="mb-3">
                                        <label htmlFor="stockValuationMethod" className="form-label">Stock Valuation Method</label>
                                        <select
                                            id="stockValuationMethod"
                                            name="stockValuationMethod"
                                            className="form-control"
                                            value={inventorySetup.stockValuationMethod}
                                            onChange={handleChange}
                                        >
                                            <option value="FIFO">FIFO</option>
                                            <option value="LIFO">LIFO</option>
                                            <option value="WeightedAverage">Weighted Average</option>
                                        </select>
                                    </div>

                                    {/* Consider Sales Return In Stock Valuation */}
                                    <div className="mb-3">
                                        <label htmlFor="considerSalesReturnInStockValuation" className="form-label">Consider Sales Return In Stock Valuation</label>
                                        <input
                                            type="checkbox"
                                            id="considerSalesReturnInStockValuation"
                                            name="considerSalesReturnInStockValuation"
                                            className="form-check-input"
                                            checked={inventorySetup.considerSalesReturnInStockValuation}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    {/* Item Coding Configuration */}
                                    <div className="mb-3">
                                        <label htmlFor="itemCodingConfiguration" className="form-label">Item Coding Configuration</label>
                                        <select
                                            id="itemCodingConfiguration"
                                            name="itemCodingConfiguration"
                                            className="form-control"
                                            value={inventorySetup.itemCodingConfiguration}
                                            onChange={handleChange}
                                        >
                                            <option value="AutoNumeric">Auto Numeric</option>
                                            <option value="Manual">Manual</option>
                                        </select>
                                    </div>

                                    {/* Duplicate Item Based On */}
                                    <div className="mb-3">
                                        <label htmlFor="duplicateItemBasedOn" className="form-label">Duplicate Item Based On</label>
                                        <select
                                            id="duplicateItemBasedOn"
                                            name="duplicateItemBasedOn"
                                            className="form-control"
                                            value={inventorySetup.duplicateItemBasedOn}
                                            onChange={handleChange}
                                        >
                                            <option value="NotRequired">Not Required</option>
                                            <option value="ItemName">Item Name</option>
                                        </select>
                                    </div>

                                    {/* Item Selection */}
                                    <div className="mb-3">
                                        <label htmlFor="itemSelection" className="form-label">Item Selection</label>
                                        <select
                                            id="itemSelection"
                                            name="itemSelection"
                                            className="form-control"
                                            value={inventorySetup.itemSelection}
                                            onChange={handleChange}
                                        >
                                            <option value="ItemLevel">Item Level</option>
                                            <option value="CategoryLevel">Category Level</option>
                                        </select>
                                    </div>

                                    {/* Separate Print Description */}
                                    <div className="mb-3">
                                        <label htmlFor="separatePrintDescription" className="form-label">Separate Print Description</label>
                                        <input
                                            type="checkbox"
                                            id="separatePrintDescription"
                                            name="separatePrintDescription"
                                            className="form-check-input"
                                            checked={inventorySetup.separatePrintDescription}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="text-end">
                                        <button type="submit" className="btn btn-primary">
                                            Update Inventory Setup
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default EditInventorySetup;
